import {
  ActionIcon,
  Anchor,
  Badge,
  Button,
  Card,
  Chip,
  Grid,
  Group,
  Menu,
  Modal,
  ScrollArea,
  Stack,
  TagsInput,
  TagsInputProps,
  Text,
  TextInput,
  ThemeIcon,
  Tooltip,
} from "@mantine/core"
import { UseFormReturnType } from "@mantine/form"
import { useDisclosure } from "@mantine/hooks"
import {
  IconCalendar,
  IconCreditCard,
  IconDots,
  IconEdit,
  IconExternalLink,
  IconGauge,
  IconListNumbers,
  IconMapPin,
  IconSearch,
  IconServer,
  IconSum,
  IconTrash,
  IconLabel,
  IconBookmark,
  IconBookmarkFilled,
  IconPlus,
  IconX,
  IconBrandGithub,
  IconCloud,
  IconTag,
} from "@tabler/icons-react"
import { UseQueryResult } from "@tanstack/react-query"
import _ from "lodash"
import { useState } from "react"

import {
  EventsRequests,
  EventsResponses,
} from "@costory/types/endpoints/events"
import { Filters, defaultSearchEvents } from "@costory/types/filters"

import { DEFAULT_DATE_FORMAT } from "@costory/shared/const"
import dayjs, { Dayjs } from "@costory/shared/dayjs"
import { filterEventsByDateRange } from "@costory/shared/utils/dateRange"

import { QueryWrapper } from "@costory/front/components/layout/QueryWrapper"
import { useChartContext } from "@costory/front/contexts/ChartContext"
import { EventsModalChips } from "@costory/front/pages/Events/EventsModalChips"
import { NewEventForm } from "@costory/front/pages/Events/NewEventForm"
import { EditEventForm } from "@costory/front/pages/Events/UpdateEvent"
import { useCheckBillingAWSQuery } from "@costory/front/queries/billingDatasources"
import {
  useDeleteEventMutation,
  useGithubReposQuery,
  useTagsQuery,
} from "@costory/front/queries/events"
import { useEventsQuery } from "@costory/front/queries/explore"
import { disableAllEventSources } from "@costory/front/utils/charts"
import { IconsEventsCategory } from "@costory/front/utils/events"

// Helper to get icon for a label
const getLabelIcon = (label: string) => {
  if (label.toLowerCase().includes("offering_type")) {
    return (
      <ThemeIcon size="xs" variant="light" color="green">
        <IconTag size={10} />
      </ThemeIcon>
    )
  }
  if (label.toLowerCase().includes("service_name")) {
    return (
      <ThemeIcon size="xs" variant="light" color="red">
        <IconLabel size={10} />
      </ThemeIcon>
    )
  }
  if (label.toLowerCase().includes("family")) {
    return (
      <ThemeIcon size="xs" variant="light" color="violet">
        <IconServer size={10} />
      </ThemeIcon>
    )
  }
  if (label.toLowerCase().includes("payment")) {
    return (
      <ThemeIcon size="xs" variant="light" color="green">
        <IconCreditCard size={10} />
      </ThemeIcon>
    )
  }
  if (label.toLowerCase().includes("term")) {
    return (
      <ThemeIcon size="xs" variant="light" color="orange">
        <IconCalendar size={10} />
      </ThemeIcon>
    )
  }
  if (
    label.toLowerCase().includes("region") ||
    label.toLowerCase().includes("zone") ||
    label.toLowerCase().includes("regions")
  ) {
    return (
      <ThemeIcon size="xs" variant="light" color="red">
        <IconMapPin size={10} />
      </ThemeIcon>
    )
  }
  if (label.toLowerCase().includes("number")) {
    return (
      <ThemeIcon size="xs" variant="light" color="green">
        <IconListNumbers size={10} />
      </ThemeIcon>
    )
  }
  if (
    label.toLowerCase().includes("subscription") ||
    label.toLowerCase().includes("credit_id")
  ) {
    return (
      <ThemeIcon size="xs" variant="light" color="green">
        <IconTag size={10} />
      </ThemeIcon>
    )
  }
  if (label.toLowerCase().includes("total")) {
    return (
      <ThemeIcon size="xs" variant="light" color="orange">
        <IconSum size={10} />
      </ThemeIcon>
    )
  }
  if (label.toLowerCase().includes("unused")) {
    return (
      <ThemeIcon size="xs" variant="light" color="cyan">
        <IconGauge size={10} />
      </ThemeIcon>
    )
  }
  return null
}

const _EventsList = ({
  events,
  searchValue,
  onClose,
  form,
  selectedDateRange,
  currentView,
  advancedExplorerForm,
  fromReferenceArea,
}: {
  events: EventsResponses.Aggregated
  searchValue: string
  onClose: () => void
  form: UseFormReturnType<Filters>
  selectedDateRange?: { from: Dayjs; to: Dayjs }
  currentView: Filters
  advancedExplorerForm?: UseFormReturnType<Filters>
  fromReferenceArea?: boolean
}) => {
  const { filterForm, setURLFilters } = useChartContext()
  const formWithBookmarkedEvents = advancedExplorerForm ?? filterForm
  const bookmarkedEventsCount =
    advancedExplorerForm?.getValues().bookmarkedEvents?.length ?? 0
  const bookmarkDisabled = bookmarkedEventsCount >= 2
  const { mutateAsync: deleteEvent } = useDeleteEventMutation()

  // The backend now handles all filtering and merging of bookmarked events
  // We can simply use all events from the response
  const selectedEvents = events.technicalEvents
    .concat(events.businessEvents)
    .concat(events.providerEvents)
    .map((el) => el.events)
    .flat() as EventsRequests.Event[]

  const updateBookmarkedEvents = async (eventId: string, add: boolean) => {
    try {
      const prev = formWithBookmarkedEvents.getValues().bookmarkedEvents || []
      const prevArray = Array.isArray(prev) ? prev : []
      const next = add
        ? [...new Set([...prevArray, eventId])]
        : prevArray.filter((id: string) => id !== eventId)

      // Update bookmarked events
      form.setFieldValue("bookmarkedEvents", next)
      if (advancedExplorerForm) {
        advancedExplorerForm.setFieldValue("bookmarkedEvents", next)
      } else {
        setURLFilters({ ...filterForm.getValues(), bookmarkedEvents: next })
      }

      // When adding a bookmark, disable all event sources to show only bookmarked events
      if (add) {
        disableAllEventSources(form)
      }
    } catch (error) {
      console.error("Failed to update bookmarked events:", error)
    }
  }
  const [isEditEventOpen, setIsEditEventOpen] = useState(false)
  const [eventToEdit, setEventToEdit] = useState<EventsRequests.Event | null>(
    null,
  )

  // Apply both date and search filters in a single pass
  const filteredEvents = filterEvents(
    selectedEvents,
    searchValue,
    selectedDateRange,
    fromReferenceArea,
  )
  const tooltipLabel = (isBookmarked?: boolean) => {
    return bookmarkDisabled && !isBookmarked
      ? "Only two events can be bookmarked in Digest view"
      : isBookmarked
        ? "Remove bookmark"
        : "Bookmark event"
  }

  return (
    <>
      {eventToEdit && (
        <EditEventForm
          data={eventToEdit}
          isOpen={isEditEventOpen}
          onClose={() => {
            setIsEditEventOpen(false)
            setEventToEdit(null)
          }}
          currentView={currentView}
          selectedDateRange={selectedDateRange}
        />
      )}

      <ScrollArea h="65vh">
        <div key={filteredEvents.map((e) => e.id).join(",")}>
          <Stack gap="xs">
            {_(filteredEvents)
              .sortBy((a) => a.date)
              .reverse()
              .value()
              .map((event) => {
                return (
                  <Card withBorder key={event.id ?? event.name} p="md">
                    <Stack gap="xs">
                      <Group justify="space-between">
                        <Group align="center">
                          <IconsEventsCategory category={event.category} />
                          <Text fw={500} size="md">
                            {event.name} (
                            {dayjs(event.date).format(DEFAULT_DATE_FORMAT)})
                            {event.metadata?.link && (
                              <Anchor
                                href={event.metadata.link}
                                target="_blank"
                                data-intercom-target="costory-tour-explorer-event-external-link"
                              >
                                <IconExternalLink size={16} />
                              </Anchor>
                            )}
                          </Text>
                        </Group>
                        <Group>
                          {event.metadata?.source === "custom" && (
                            <Menu position="bottom-end" withinPortal>
                              <Menu.Target>
                                <ActionIcon
                                  size="lg"
                                  variant="subtle"
                                  color="textMuted"
                                  data-intercom-target="costory-tour-explorer-event-menu"
                                >
                                  <IconDots size={16} />
                                </ActionIcon>
                              </Menu.Target>
                              <Menu.Dropdown>
                                <Menu.Item
                                  color="red"
                                  leftSection={<IconTrash size={16} />}
                                  onClick={() => {
                                    if (event.id) {
                                      deleteEvent(event.id)
                                    }
                                  }}
                                  disabled={event.isBookmarked}
                                  data-intercom-target="costory-tour-explorer-event-delete"
                                >
                                  Delete Event
                                </Menu.Item>
                                <Menu.Item
                                  leftSection={<IconEdit size={16} />}
                                  onClick={() => {
                                    setEventToEdit(event)
                                    setIsEditEventOpen(true)
                                  }}
                                  data-intercom-target="costory-tour-explorer-event-edit"
                                >
                                  Edit Event
                                </Menu.Item>
                              </Menu.Dropdown>
                            </Menu>
                          )}
                          <Tooltip
                            label={tooltipLabel(event.isBookmarked)}
                            withArrow
                          >
                            <Button
                              color={event.isBookmarked ? "yellow" : undefined}
                              disabled={bookmarkDisabled && !event.isBookmarked}
                              data-intercom-target="costory-tour-explorer-event-bookmark"
                              variant="outline"
                              size="xs"
                              leftSection={
                                event.isBookmarked ? (
                                  <IconBookmarkFilled size={18} />
                                ) : (
                                  <IconBookmark size={18} />
                                )
                              }
                              onClick={async () => {
                                await updateBookmarkedEvents(
                                  event.id!,
                                  !event.isBookmarked,
                                )
                                onClose()
                              }}
                            >
                              Add to chart
                            </Button>
                          </Tooltip>
                        </Group>
                      </Group>
                      <Group justify="space-between">
                        <Stack>
                          <Text
                            size="sm"
                            c="dimmed"
                            style={{
                              textOverflow: "ellipsis",
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              maxWidth: "70vw",
                            }}
                          >
                            {event.description}
                          </Text>
                        </Stack>
                      </Group>
                      <Group justify="end">
                        <Text size="sm" c="dimmed">
                          <Group gap="xs" justify="end">
                            {_.chain(event.labels || [])
                              .filter((label) => typeof label === "string")
                              .uniq()
                              .value()
                              .map((label) => (
                                <Badge
                                  key={label}
                                  size="sm"
                                  variant="light"
                                  style={{
                                    cursor: "pointer",
                                    userSelect: "text",
                                  }}
                                  leftSection={getLabelIcon(label)}
                                  onClick={() => {
                                    form.insertListItem(
                                      "eventsFilter.labels",
                                      label,
                                    )
                                  }}
                                  data-intercom-target="costory-tour-explorer-event-label"
                                >
                                  {label}
                                </Badge>
                              ))}
                          </Group>
                        </Text>
                      </Group>
                    </Stack>
                  </Card>
                )
              })}

            {filteredEvents.length === 0 && (
              <Group justify="center" align="center">
                <Text size="sm" c="dimmed" mt={0}>
                  No events found
                </Text>
              </Group>
            )}
          </Stack>
        </div>
      </ScrollArea>
    </>
  )
}

function _ModalZoomOnEvents({
  showSuggestedEvents,
  toggleSuggestedEvents,
  eventsQuery,
  onClose,
  selectedDateRange,
  openNewEvent,
  onCloseNewEvent,
  currentView,
  advancedExplorerForm,
  fromReferenceArea,
}: {
  showSuggestedEvents?: boolean
  toggleSuggestedEvents?: () => void
  eventsQuery: UseQueryResult<EventsResponses.Aggregated>
  onClose: () => void
  selectedDateRange?: { from: Dayjs; to: Dayjs }
  openNewEvent: boolean
  onCloseNewEvent: () => void
  currentView: Filters
  advancedExplorerForm?: UseFormReturnType<Filters>
  fromReferenceArea?: boolean
}) {
  const [searchValue, setSearchValue] = useState("")
  const { savedView, filterForm } = useChartContext()
  const queryRepo = useGithubReposQuery()
  const currentForm = advancedExplorerForm || filterForm
  const query = useTagsQuery()
  const checkAWSQuery = useCheckBillingAWSQuery()

  return (
    <>
      <Stack gap="md">
        <Grid align="center">
          <Grid.Col span={8}>
            <TextInput
              placeholder="Search events..."
              leftSection={<IconSearch size={16} />}
              value={searchValue}
              onChange={(e) => setSearchValue(e.currentTarget.value)}
              data-intercom-target="costory-tour-explorer-events-search"
            />
          </Grid.Col>
          <Grid.Col span={4}>
            <QueryWrapper query={query}>
              {({ data }) => {
                const renderTagsInputOption: TagsInputProps["renderOption"] = ({
                  option,
                }) => {
                  const val = data.labels?.find(
                    (el) => el.value === option.value,
                  )
                  return (
                    <Group>
                      <Text span>
                        {val?.source === "github" ? (
                          <IconBrandGithub size={15} />
                        ) : val?.source === "provider" ? (
                          <IconCloud size={15} />
                        ) : (
                          <IconTag size={15} />
                        )}
                      </Text>
                      <Text>{option.value}</Text>
                    </Group>
                  )
                }

                return (
                  <TagsInput
                    {...currentForm.getInputProps("eventsFilter.labels")}
                    data={
                      data.labels.map((el) => ({
                        value: el.value,
                        label: el.value,
                      })) ?? []
                    }
                    renderOption={renderTagsInputOption}
                    clearable
                    placeholder="Filter by labels"
                    data-intercom-target="costory-tour-explorer-event-labels"
                    radius="md"
                    key={
                      currentForm
                        .getValues()
                        ?.eventsFilter?.labels?.join(",") ?? ""
                    }
                  />
                )
              }}
            </QueryWrapper>
          </Grid.Col>
        </Grid>

        {/* Filter Chips */}
        <EventsModalChips
          showSuggestedEvents={showSuggestedEvents}
          toggleSuggestedEvents={toggleSuggestedEvents}
          form={currentForm}
          eventsData={eventsQuery.data}
          githubRepos={queryRepo.data}
          selectedDateRange={selectedDateRange}
          isAWSPresent={checkAWSQuery.data}
        />
        <EventsList
          eventsQuery={eventsQuery}
          searchValue={searchValue}
          onClose={onClose}
          form={currentForm}
          selectedDateRange={selectedDateRange}
          currentView={currentView}
          advancedExplorerForm={advancedExplorerForm}
          fromReferenceArea={fromReferenceArea}
        />
      </Stack>
      {openNewEvent && (
        <NewEventForm
          isOpen={openNewEvent}
          onClose={onCloseNewEvent}
          defaultCategory={undefined}
          defaultDate={selectedDateRange?.from.format(DEFAULT_DATE_FORMAT)}
          savedViewId={savedView?.id}
          selectedDateRange={selectedDateRange}
          currentView={currentView}
        />
      )}
    </>
  )
}

export const ModalZoomOnEvents = ({
  filters,
  isOpened,
  onClose,
  bookmarkedEvents,
  selectedDateRange,
  currentView,
  advancedExplorerForm,
  fromReferenceArea,
  showSuggestedEvents,
  toggleSuggestedEvents,
}: {
  filters: EventsRequests.Search["filters"]
  isOpened: boolean
  onClose: () => void
  bookmarkedEvents: string[]
  openNewModal: () => void
  selectedDateRange?: { from: Dayjs; to: Dayjs }
  currentView: Filters
  advancedExplorerForm?: UseFormReturnType<Filters>
  fromReferenceArea?: boolean
  showSuggestedEvents?: boolean
  toggleSuggestedEvents?: () => void
}) => {
  const [mutableDateRange, setMutableDateRange] = useState(selectedDateRange)
  const { filterForm } = useChartContext()
  const [openInternalNewEvent, newEventHandlers] = useDisclosure(false)
  const currentForm = advancedExplorerForm || filterForm
  const eventsFilter =
    currentForm.getValues().eventsFilter || defaultSearchEvents

  const eventsQuery = useEventsQuery({
    searchForm: {
      filters,
      searchEvents: eventsFilter,
      bookmarkedEvents,
    },
  })

  const handleDismissDateRange = () => {
    setMutableDateRange(undefined)
  }

  const handleNewEventClick = () => {
    newEventHandlers.open()
  }

  const DateRangeChips = () => {
    if (!mutableDateRange) return null

    const { from, to } = mutableDateRange
    if (fromReferenceArea) {
      const startRangeStart = from.subtract(3, "day")
      const startRangeEnd = from.add(3, "day")
      const endRangeStart = to.subtract(3, "day")
      const endRangeEnd = to.add(3, "day")

      return (
        <>
          <RenderChip
            label={`${startRangeStart.format(DEFAULT_DATE_FORMAT)} to ${startRangeEnd.format(DEFAULT_DATE_FORMAT)}`}
            onClick={handleDismissDateRange}
          />
          <RenderChip
            label={`${endRangeStart.format(DEFAULT_DATE_FORMAT)} to ${endRangeEnd.format(DEFAULT_DATE_FORMAT)}`}
            onClick={handleDismissDateRange}
          />
        </>
      )
    }

    const label = from.isSame(to, "day")
      ? from.format(DEFAULT_DATE_FORMAT)
      : `${from.format(DEFAULT_DATE_FORMAT)} to ${to.format(DEFAULT_DATE_FORMAT)}`

    return <RenderChip label={label} onClick={handleDismissDateRange} />
  }

  return (
    <Modal.Root
      opened={isOpened}
      onClose={() => {
        newEventHandlers.close()
        onClose()
      }}
      size="90%"
    >
      <Modal.Overlay />
      <Modal.Content>
        <Stack>
          <Group justify="space-between">
            <Group justify="flex-start" style={{ marginLeft: 24 }}>
              <Text fw={600} size="lg">
                Events
              </Text>

              <Button
                variant="light"
                color="blue"
                size="xs"
                leftSection={<IconPlus size={14} />}
                onClick={handleNewEventClick}
                data-intercom-target="costory-tour-explorer-new-event"
              >
                New
              </Button>
            </Group>
            <Group justify="flex-end">
              <DateRangeChips />
              <Modal.CloseButton />
            </Group>
          </Group>

          <Modal.Body>
            <_ModalZoomOnEvents
              showSuggestedEvents={showSuggestedEvents}
              toggleSuggestedEvents={toggleSuggestedEvents}
              eventsQuery={eventsQuery}
              onClose={onClose}
              selectedDateRange={mutableDateRange}
              openNewEvent={openInternalNewEvent}
              onCloseNewEvent={newEventHandlers.close}
              currentView={currentView}
              advancedExplorerForm={advancedExplorerForm}
              fromReferenceArea={fromReferenceArea}
            />
          </Modal.Body>
        </Stack>
      </Modal.Content>
    </Modal.Root>
  )
}

const checkIfEventMatchesSearch = (
  event: EventsRequests.Event,
  searchValue: string,
) => {
  const searchTerms = searchValue.trim().split("+")
  return searchTerms.some((word) => {
    const labels = event.labels || []
    const onLabelMatches = labels.some((label: string) => {
      return word.toLowerCase().includes(label.toLowerCase())
    })
    const eventName = event.name
    const eventDescription = event.description
    const eventDate = dayjs(event.date)

    return (
      eventName.toLowerCase().includes(word.toLowerCase()) ||
      eventDescription.toLowerCase().includes(word.toLowerCase()) ||
      onLabelMatches ||
      eventDate
        .format(DEFAULT_DATE_FORMAT)
        .toLowerCase()
        .includes(word.toLowerCase())
    )
  })
}

function filterEvents(
  events: EventsRequests.Event[],
  searchValue: string,
  selectedDateRange?: { from: Dayjs; to: Dayjs },
  fromReferenceArea?: boolean,
) {
  if (selectedDateRange) {
    if (fromReferenceArea) {
      const startRangeStart = selectedDateRange.from.subtract(3, "day")
      const startRangeEnd = selectedDateRange.from.add(3, "day")

      const endRangeStart = selectedDateRange.to.subtract(3, "day")
      const endRangeEnd = selectedDateRange.to.add(3, "day")

      events = events.filter((event) => {
        const eventDate = dayjs(event.date)
        return (
          eventDate.isSame(startRangeStart, "day") ||
          eventDate.isSame(startRangeEnd, "day") ||
          (eventDate.isAfter(startRangeStart) &&
            eventDate.isBefore(startRangeEnd)) ||
          eventDate.isSame(endRangeStart, "day") ||
          eventDate.isSame(endRangeEnd, "day") ||
          (eventDate.isAfter(endRangeStart) && eventDate.isBefore(endRangeEnd))
        )
      })
    } else {
      events = filterEventsByDateRange(events, selectedDateRange)
    }
  }
  events = events.filter((el) => checkIfEventMatchesSearch(el, searchValue))
  return events
}

const EventsList = ({
  eventsQuery,
  searchValue,
  onClose,
  form,
  selectedDateRange,
  currentView,
  advancedExplorerForm,
  fromReferenceArea,
}: {
  eventsQuery: UseQueryResult<EventsResponses.Aggregated>
  searchValue: string
  onClose: () => void
  form: UseFormReturnType<Filters>
  selectedDateRange?: { from: Dayjs; to: Dayjs }
  currentView: Filters
  advancedExplorerForm?: UseFormReturnType<Filters>
  fromReferenceArea?: boolean
}) => {
  return (
    <QueryWrapper query={eventsQuery}>
      {({ data }) => (
        <_EventsList
          events={data}
          searchValue={searchValue}
          onClose={onClose}
          form={form}
          selectedDateRange={selectedDateRange}
          currentView={currentView}
          advancedExplorerForm={advancedExplorerForm}
          fromReferenceArea={fromReferenceArea}
        />
      )}
    </QueryWrapper>
  )
}

const RenderChip = ({
  label,
  onClick,
}: {
  label: string
  onClick: () => void
}) => (
  <Chip
    checked
    color="blue"
    variant="light"
    icon={<IconX size={14} />}
    onClick={onClick}
    styles={{
      root: { marginRight: 12 },
    }}
  >
    {label}
  </Chip>
)
